import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, RefreshCw, Save, Banknote, Check, X as XIcon, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RANGES = [7, 30, 90];
const fmt = (n) => Number(n || 0).toFixed(2);

export const DailySnapshotsPanel = () => {
    const [days, setDays] = useState(30);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [closing, setClosing] = useState(false);
    const [editingDate, setEditingDate] = useState(null);
    const [editBank, setEditBank] = useState('');
    const [editNote, setEditNote] = useState('');
    const [savingRecon, setSavingRecon] = useState(false);
    const [reconSummary, setReconSummary] = useState(null);
    const [summaryMonth, setSummaryMonth] = useState(null);  // YYYY-MM ; null => current CT month

    const fetchSnapshots = async (n = days) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/snapshots?days=${n}`, { withCredentials: true });
            setSnapshots(data.snapshots || []);
        } catch {
            toast.error('Failed to load snapshots');
        }
        setLoading(false);
    };

    const fetchReconSummary = async (m = summaryMonth) => {
        try {
            const url = m
                ? `${API}/admin/snapshots/reconcile-summary?month=${m}`
                : `${API}/admin/snapshots/reconcile-summary`;
            const { data } = await axios.get(url, { withCredentials: true });
            setReconSummary(data);
        } catch {
            // silent — summary is optional
        }
    };

    useEffect(() => { fetchSnapshots(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);
    useEffect(() => { fetchReconSummary(summaryMonth); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [summaryMonth, snapshots]);

    const shiftMonth = (delta) => {
        const base = summaryMonth
            ? new Date(summaryMonth + '-01T12:00:00Z')
            : new Date();
        base.setUTCMonth(base.getUTCMonth() + delta);
        const y = base.getUTCFullYear();
        const m = String(base.getUTCMonth() + 1).padStart(2, '0');
        setSummaryMonth(`${y}-${m}`);
    };

    const handleCloseToday = async () => {
        setClosing(true);
        try {
            const { data } = await axios.post(`${API}/admin/snapshots/close-day`, {}, { withCredentials: true });
            toast.success(data.message);
            fetchSnapshots(days);
        } catch {
            toast.error('Failed to close day');
        }
        setClosing(false);
    };

    const startEditRecon = (s) => {
        setEditingDate(s.date);
        setEditBank(s.bank_deposit_total != null ? String(s.bank_deposit_total) : '');
        setEditNote(s.bank_note || '');
    };

    const cancelEditRecon = () => {
        setEditingDate(null);
        setEditBank('');
        setEditNote('');
    };

    const saveRecon = async (date, clear = false) => {
        setSavingRecon(true);
        try {
            const payload = clear
                ? { bank_deposit_total: null, bank_note: '' }
                : { bank_deposit_total: parseFloat(editBank), bank_note: editNote.trim() };
            if (!clear && (isNaN(payload.bank_deposit_total) || payload.bank_deposit_total < 0)) {
                toast.error('Enter a valid bank deposit amount');
                setSavingRecon(false);
                return;
            }
            const { data } = await axios.put(
                `${API}/admin/snapshots/${date}/reconcile`,
                payload,
                { withCredentials: true }
            );
            toast.success(data.message);
            cancelEditRecon();
            fetchSnapshots(days);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reconcile failed');
        }
        setSavingRecon(false);
    };

    // Simple bar chart: max(|cash_profit|) defines scale
    const maxAbs = Math.max(1, ...snapshots.map(s => Math.abs(s.cash_profit || 0)));

    const totals = snapshots.reduce((acc, s) => {
        acc.loaded   += s.loaded_in?.total || 0;
        acc.redeemed += s.redeemed_out?.total || 0;
        acc.free_play+= s.free_play?.total || 0;
        acc.profit   += s.cash_profit || 0;
        acc.bets     += s.total_bets || 0;
        return acc;
    }, { loaded: 0, redeemed: 0, free_play: 0, profit: 0, bets: 0 });

    return (
        <div className="card-dark p-6 mt-6 border-[#3B82F6]/20" data-testid="daily-snapshots-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <CalendarClock className="w-4 h-4 text-[#3B82F6]" />
                        <h3 className="text-base font-bold text-white">Daily History (Snapshots)</h3>
                    </div>
                    <p className="text-[11px] text-[#A0AAB2] mt-1 max-w-xl">
                        Close-of-day archive: each row is a frozen daily ledger (auto-saved at midnight CT). Use this for trend spotting + bank reconciliation.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                        {RANGES.map(n => (
                            <button
                                type="button"
                                key={n}
                                onClick={() => setDays(n)}
                                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${
                                    days === n
                                        ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                        : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40'
                                }`}
                                data-testid={`snap-range-${n}`}>
                                {n}d
                            </button>
                        ))}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => fetchSnapshots(days)} disabled={loading}
                        className="border-[#FFD700]/30 text-[#FFD700] h-8" data-testid="snap-refresh-btn">
                        <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button size="sm" onClick={handleCloseToday} disabled={closing}
                        className="bg-[#3B82F6] hover:bg-[#3B82F6]/90 text-white font-bold h-8"
                        data-testid="snap-close-today-btn">
                        <Save className="w-3 h-3 mr-1" /> {closing ? 'Closing…' : 'Close Today'}
                    </Button>
                </div>
            </div>

            {/* Reconciliation Summary (current/selected month) */}
            {reconSummary && (
                <div className="rounded-lg border border-[#FFD700]/20 bg-gradient-to-br from-[#FFD700]/5 to-transparent p-4 mb-4" data-testid="recon-summary-card">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <Banknote className="w-4 h-4 text-[#FFD700]" />
                            <h4 className="text-sm font-bold text-white">Reconciliation Summary</h4>
                            <span className="text-[10px] text-[#A0AAB2]">{reconSummary.month}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => shiftMonth(-1)}
                                className="border-white/10 text-white h-7 px-2"
                                data-testid="recon-summary-prev-month">
                                <ChevronLeft className="w-3 h-3" />
                            </Button>
                            {summaryMonth && (
                                <Button size="sm" variant="outline" onClick={() => setSummaryMonth(null)}
                                    className="border-white/10 text-white h-7 text-[10px] px-2">
                                    Current
                                </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => shiftMonth(1)}
                                className="border-white/10 text-white h-7 px-2"
                                data-testid="recon-summary-next-month">
                                <ChevronRight className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3 text-xs">
                        <Stat label="Days Snapped"  value={`${reconSummary.total_days_with_snapshot}`} color="#FFFFFF" />
                        <Stat label="Reconciled"    value={`${reconSummary.days_reconciled}`}          color="#3B82F6" />
                        <Stat label="Unreconciled"  value={`${reconSummary.days_unreconciled}`}        color={reconSummary.days_unreconciled > 0 ? '#FFD700' : '#A0AAB2'} />
                        <Stat label="Matches"       value={`${reconSummary.days_match}`}               color="#39FF14" />
                        <Stat label="Off"           value={`${reconSummary.days_off}`}                 color={reconSummary.days_off > 0 ? '#FF3B30' : '#A0AAB2'} />
                        <Stat label="Net Variance"  value={`${reconSummary.total_variance >= 0 ? '+' : ''}${fmt(reconSummary.total_variance)}`}
                            color={Math.abs(reconSummary.total_variance) < 0.01 ? '#39FF14' : '#FF3B30'} bold />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs">
                        <div className="bg-[#0a0a0a] rounded-lg p-2.5 flex justify-between">
                            <span className="text-[#A0AAB2]">Total Expected</span>
                            <span className="text-white font-mono font-bold">${fmt(reconSummary.total_expected)}</span>
                        </div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2.5 flex justify-between">
                            <span className="text-[#A0AAB2]">Total Banked</span>
                            <span className="text-white font-mono font-bold">${fmt(reconSummary.total_banked)}</span>
                        </div>
                    </div>
                    {reconSummary.top_variances && reconSummary.top_variances.length > 0 && (
                        <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/5">
                            <p className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide mb-2 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3 text-[#FFD700]" />
                                Top Variance Days This Month
                            </p>
                            <div className="space-y-1.5" data-testid="recon-summary-top-variances">
                                {reconSummary.top_variances.map(v => {
                                    const isMatch = Math.abs(v.variance) < 0.01;
                                    return (
                                        <div key={v.date} className="flex items-center justify-between text-xs">
                                            <span className="text-white font-mono">{v.date}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] text-[#A0AAB2]">exp ${fmt(v.expected)}</span>
                                                <span className="text-[10px] text-[#A0AAB2]">vs bank ${fmt(v.bank)}</span>
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                                    isMatch ? 'bg-[#39FF14]/15 text-[#39FF14]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'
                                                }`}>
                                                    {isMatch ? '✓' : `${v.variance >= 0 ? '+' : ''}${fmt(v.variance)}`}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {reconSummary.days_unreconciled > 0 && (
                        <p className="text-[10px] text-[#FFD700] mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {reconSummary.days_unreconciled} day(s) still need reconciliation in {reconSummary.month}.
                        </p>
                    )}
                </div>
            )}
            {snapshots.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4 text-xs">
                    <Stat label={`Loaded (${days}d)`}   value={`+${fmt(totals.loaded)}`}   color="#39FF14" />
                    <Stat label={`Redeemed: cashout requests (${days}d)`} value={`-${fmt(totals.redeemed)}`} color="#FF3B30" />
                    <Stat label={`Free Play (${days}d)`}value={`-${fmt(totals.free_play)}`}color="#A855F7" />
                    <Stat label={`Bets (${days}d)`}     value={`${totals.bets}`}            color="#FFD700" />
                    <Stat label={`Net after free play, not profit (${days}d)`} value={`${totals.profit >= 0 ? '+' : ''}${fmt(totals.profit)}`}
                        color={totals.profit >= 0 ? '#39FF14' : '#FF3B30'} bold />
                </div>
            )}

            {/* Mini chart */}
            {snapshots.length > 0 && (
                <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/5 mb-4">
                    <p className="text-[10px] uppercase font-bold text-[#A0AAB2] mb-2 tracking-wide">Net after free play by day (loaded − redeemed − free play; not profit)</p>
                    <div className="flex items-end gap-1 h-24" data-testid="snap-chart">
                        {snapshots.map((s) => {
                            const height = Math.max(2, Math.round((Math.abs(s.cash_profit || 0) / maxAbs) * 100));
                            const positive = (s.cash_profit || 0) >= 0;
                            return (
                                <div key={s.date}
                                    className="flex-1 min-w-0 flex flex-col items-center justify-end group relative cursor-default">
                                    <div
                                        className={`w-full rounded-t transition-all ${
                                            positive ? 'bg-[#39FF14]/70 hover:bg-[#39FF14]' : 'bg-[#FF3B30]/70 hover:bg-[#FF3B30]'
                                        }`}
                                        style={{ height: `${height}%` }}
                                        title={`${s.date}: ${positive ? '+' : ''}${fmt(s.cash_profit)}`}
                                    />
                                    <div className="hidden group-hover:block absolute -top-12 left-1/2 -translate-x-1/2 bg-black border border-white/20 rounded px-2 py-1 text-[10px] whitespace-nowrap z-10">
                                        <span className="text-white font-bold">{s.date}</span>
                                        <span className={`ml-1.5 font-mono ${positive ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                            {positive ? '+' : ''}{fmt(s.cash_profit)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-between mt-1 text-[9px] text-[#666]">
                        <span>{snapshots[0]?.date}</span>
                        <span>{snapshots[snapshots.length - 1]?.date}</span>
                    </div>
                </div>
            )}

            {/* Table */}
            {loading && snapshots.length === 0 ? (
                <div className="text-center py-8 text-[#A0AAB2] text-sm">Loading…</div>
            ) : snapshots.length === 0 ? (
                <div className="text-center py-8 text-[#A0AAB2] text-sm">
                    No snapshots yet. Click <span className="text-[#FFD700] font-bold">Close Today</span> to save your first one, or wait until after midnight CT for the auto-close to run.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-white/5">
                    <table className="w-full text-xs">
                        <thead className="bg-[#0a0a0a] text-[#A0AAB2]">
                            <tr>
                                <th className="text-left p-2">Date</th>
                                <th className="text-right p-2">Loaded</th>
                                <th className="text-right p-2" title="Cashout requests made that day (not cash paid; see Money summary for cash paid)">Redeemed (requests)</th>
                                <th className="text-right p-2">Free Play</th>
                                <th className="text-right p-2">Bets</th>
                                <th className="text-right p-2">Active</th>
                                <th className="text-right p-2">Net after free play</th>
                                <th className="text-left p-2">Bank Recon</th>
                                <th className="text-right p-2">Closed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...snapshots].reverse().map(s => {
                                const positive = (s.cash_profit || 0) >= 0;
                                const expected = s.expected_bank ?? ((s.loaded_in?.total || 0) - (s.redeemed_out?.total || 0));
                                const bankSet = s.bank_deposit_total != null;
                                const variance = bankSet ? (s.bank_deposit_total - expected) : null;
                                const isMatch = bankSet && Math.abs(variance) < 0.01;
                                const isEditing = editingDate === s.date;
                                return (
                                    <tr key={s.date} className="border-t border-white/5 hover:bg-white/5"
                                        data-testid={`snap-row-${s.date}`}>
                                        <td className="p-2 text-white font-bold whitespace-nowrap">{s.date}</td>
                                        <td className="p-2 text-right font-mono text-[#39FF14]">+{fmt(s.loaded_in?.total)}</td>
                                        <td className="p-2 text-right font-mono text-[#FF3B30]">-{fmt(s.redeemed_out?.total)}</td>
                                        <td className="p-2 text-right font-mono text-[#A855F7]">-{fmt(s.free_play?.total)}</td>
                                        <td className="p-2 text-right font-mono text-white">{s.total_bets}</td>
                                        <td className="p-2 text-right font-mono text-white">{s.active_players}</td>
                                        <td className={`p-2 text-right font-mono font-bold ${positive ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                            {positive ? '+' : ''}{fmt(s.cash_profit)}
                                        </td>
                                        <td className="p-2">
                                            {isEditing ? (
                                                <div className="flex flex-col gap-1 min-w-[260px]" data-testid={`recon-edit-${s.date}`}>
                                                    <div className="flex gap-1 items-center">
                                                        <span className="text-[10px] text-[#A0AAB2]">Expected:</span>
                                                        <span className="text-[11px] font-mono text-[#FFD700]">${fmt(expected)}</span>
                                                    </div>
                                                    <Input
                                                        type="number"
                                                        value={editBank}
                                                        onChange={e => setEditBank(e.target.value)}
                                                        placeholder="Bank deposit total"
                                                        className="bg-[#0a0a0a] border-white/10 text-white h-7 text-xs"
                                                        data-testid={`recon-bank-input-${s.date}`}
                                                        autoFocus
                                                    />
                                                    <Input
                                                        value={editNote}
                                                        onChange={e => setEditNote(e.target.value)}
                                                        placeholder="Note (optional)"
                                                        className="bg-[#0a0a0a] border-white/10 text-white h-7 text-xs"
                                                        data-testid={`recon-note-input-${s.date}`}
                                                    />
                                                    <div className="flex gap-1">
                                                        <Button size="sm" onClick={() => saveRecon(s.date)} disabled={savingRecon}
                                                            className="bg-[#FFD700] text-black font-bold h-6 px-2 text-[10px]"
                                                            data-testid={`recon-save-${s.date}`}>
                                                            <Check className="w-3 h-3 mr-0.5" />Save
                                                        </Button>
                                                        {bankSet && (
                                                            <Button size="sm" variant="outline" onClick={() => saveRecon(s.date, true)} disabled={savingRecon}
                                                                className="border-[#FF3B30]/30 text-[#FF3B30] h-6 px-2 text-[10px]"
                                                                data-testid={`recon-clear-${s.date}`}>
                                                                Clear
                                                            </Button>
                                                        )}
                                                        <Button size="sm" variant="outline" onClick={cancelEditRecon}
                                                            className="border-white/20 text-white h-6 px-2 text-[10px]">
                                                            <XIcon className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : bankSet ? (
                                                <button
                                                    type="button"
                                                    onClick={() => startEditRecon(s)}
                                                    className="text-left flex flex-col gap-0.5 hover:bg-white/5 rounded px-2 py-1 transition-colors w-full"
                                                    data-testid={`recon-display-${s.date}`}
                                                    title={s.bank_note || 'Click to edit'}
                                                >
                                                    <span className="text-[11px] font-mono text-white">${fmt(s.bank_deposit_total)}</span>
                                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold w-fit ${
                                                        isMatch ? 'bg-[#39FF14]/15 text-[#39FF14]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'
                                                    }`}>
                                                        {isMatch ? '✓ MATCH' : `OFF BY ${variance >= 0 ? '+' : ''}${fmt(variance)}`}
                                                    </span>
                                                    {s.bank_note && (
                                                        <span className="text-[9px] text-[#666] truncate max-w-[200px]">{s.bank_note}</span>
                                                    )}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => startEditRecon(s)}
                                                    className="flex items-center gap-1 text-[10px] text-[#3B82F6] hover:text-[#3B82F6]/80 hover:underline px-2 py-1"
                                                    data-testid={`recon-add-${s.date}`}>
                                                    <Banknote className="w-3 h-3" />
                                                    Reconcile…
                                                </button>
                                            )}
                                        </td>
                                        <td className="p-2 text-right">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                                s.auto ? 'bg-[#3B82F6]/15 text-[#3B82F6]' : 'bg-[#FFD700]/15 text-[#FFD700]'
                                            }`}>
                                                {s.auto ? 'AUTO' : 'MANUAL'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const Stat = ({ label, value, color, bold }) => (
    <div className="bg-[#0a0a0a] rounded-lg p-2.5">
        <p className="text-[9px] uppercase font-bold text-[#A0AAB2] tracking-wide truncate">{label}</p>
        <p className={`font-mono ${bold ? 'font-black text-base' : 'font-bold text-sm'}`} style={{ color }}>{value}</p>
    </div>
);
